import React, { useState } from 'react';
import useModalStore from "../../stores/modalStore";
import { epochToIso8601 } from "../../utils/formatting";
import LoadingSpinner from "../../LoadingSpinner";
import {LOCATION_NAME_TO_ID} from "../../utils/constants";
import useEventStore from "../../stores/eventStore";
import useUserStore from "../../stores/userStore";
import {loggedAlert} from "../../services/eventService";
import { hasPermission } from "../../utils/permissions";

const EditEventModal = ({
                            handleUpdateEvent,
                        }) => {
    const { selectedEvent, setSelectedEvent, updateSelectedEventTimestamp, updateSelectedEventLocationId } = useEventStore();
    const { userIdToFirstName, setEmployeeEvents, setRequestList } = useUserStore();
    const show = useModalStore(state => state.showEditEventModal);
    const closeModal = useModalStore(state => state.closeModal);

    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!show || !selectedEvent) return null;

    const editEventSuccessCallback = () => {
        //Edits are technically just generating a new event to override the old one.
        setEmployeeEvents(selectedEvent.user_id, (prev) => {
            const filtered = prev.filter(e => e.id !== selectedEvent.id);
            return [...filtered, { ...selectedEvent }];
        });

        setRequestList((prev) => {
            const filtered = prev.filter(e => e.id !== selectedEvent.id);
            return [...filtered, { ...selectedEvent }];
        });
    };

    const inputClass = "mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm";

    const handleClose = () => {
        setSelectedEvent((prev) => ({ ...prev, notes: '', event_timestamp: Date.now() }));
        closeModal("editEventModal");
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-800">
                        Edit Event for {userIdToFirstName[selectedEvent.user_id] || selectedEvent.user_id}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        setIsSubmitting(true);
                        try {
                            await handleUpdateEvent(e, editEventSuccessCallback, () => {
                                loggedAlert("Update event failed");
                            });
                            closeModal("editEventModal");
                        } catch (err) {
                            console.error("Update failed", err);
                            loggedAlert("Failed to update event. See console for details. Error: " + err.message || "Unknown error");
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    <div className="p-6 space-y-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Timestamp
                            <input
                                className={inputClass}
                                required
                                type="datetime-local"
                                value={epochToIso8601(selectedEvent.event_timestamp)}
                                onChange={updateSelectedEventTimestamp}
                            />
                        </label>

                        <label className="block text-sm font-medium text-gray-700">
                            Location
                            <select
                                className={inputClass}
                                value={selectedEvent.location_id}
                                onChange={updateSelectedEventLocationId}
                            >
                                <option value="" disabled>Select a location</option>
                                {Object.entries(LOCATION_NAME_TO_ID)
                                    .filter(([_, id]) => id !== null)
                                    .map(([name, id]) => (
                                        <option key={id} value={id}>{name}</option>
                                    ))}
                            </select>
                        </label>

                        <label className="block text-sm font-medium text-gray-700">
                            Notes
                            <textarea
                                className={`${inputClass} resize-y min-h-[60px]`}
                                value={selectedEvent.notes}
                                placeholder="Optional notes..."
                                onChange={(e) =>
                                    setSelectedEvent({ ...selectedEvent, notes: e.target.value })
                                }
                            />
                        </label>

                        {hasPermission('edit_events_for_others') && (
                            <label className="block text-sm font-medium text-gray-700">
                                Sequence Number
                                <input
                                    className={inputClass}
                                    type="number"
                                    value={selectedEvent.sequence_number || ""}
                                    onChange={(e) =>
                                        setSelectedEvent({
                                            ...selectedEvent,
                                            sequence_number: parseInt(e.target.value, 10)
                                        })
                                    }
                                />
                            </label>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="!px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md !transition-all !inline-flex !items-center !gap-2"
                        >
                            {isSubmitting ? <LoadingSpinner small /> : "Update Event"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEventModal;
